import mergeRefs from "merge-refs";
import type { ReactNode } from "react";
import {
  createContext,
  forwardRef,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const NARROW_CONTAINER_MAX_WIDTH = 480;

interface CommentsContainerContextValue {
  isNarrow: boolean;
}

const CommentsContainerContext =
  createContext<CommentsContainerContextValue | null>(null);
CommentsContainerContext.displayName = "CommentsContainerContext";

interface CommentsContainerProps {
  children: ReactNode;
}

// The narrow-layout breakpoint is measured from the container, not the viewport,
// so the tree adapts to its surface (modal, page, drawer). Layout decisions must
// use `useCommentsContainer`, not viewport media queries.
export const CommentsContainer = forwardRef<
  HTMLDivElement,
  CommentsContainerProps
>(({ children }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const setContainerRef = useMemo(() => mergeRefs(containerRef, ref), [ref]);
  const [isNarrow, setIsNarrow] = useState(false);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const update = () =>
      setIsNarrow(element.offsetWidth <= NARROW_CONTAINER_MAX_WIDTH);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const value = useMemo<CommentsContainerContextValue>(
    () => ({ isNarrow }),
    [isNarrow],
  );

  return (
    <CommentsContainerContext.Provider value={value}>
      <div ref={setContainerRef}>{children}</div>
    </CommentsContainerContext.Provider>
  );
});

CommentsContainer.displayName = "CommentsContainer";

export const useCommentsContainer = (): CommentsContainerContextValue => {
  const value = useContext(CommentsContainerContext);
  if (!value) {
    throw new Error(
      "useCommentsContainer must be used inside a CommentsContainer.",
    );
  }
  return value;
};
